import { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  MdImage,
  MdMic,
  MdClose,
  MdStop
} from 'react-icons/md';

const Image = MdImage as any;
const Microphone = MdMic as any;
const X = MdClose as any;
const Stop = MdStop as any;

interface ChatInputProps {
  onSendMessage: (message: string, image?: File) => void;
  disabled?: boolean;
  isVoiceActive?: boolean;
  isUserRecording?: boolean;
  onStopRecording?: () => void;
  isProcessingImage?: boolean;
}

export interface ChatInputRef {
  focus: () => void;
}

const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(({ 
  onSendMessage, 
  disabled = false, 
  isVoiceActive = false,
  isUserRecording = false,
  onStopRecording,
  isProcessingImage = false
}, ref) => {
  const [message, setMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    }
  }));

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if ((message.trim() || selectedImage) && !disabled) {
      onSendMessage(message.trim(), selectedImage || undefined);
      setMessage('');
      clearImage();
      // Reset textarea height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter key alone sends the message
    // Shift+Enter creates a new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          handleImageSelection(file);
        }
        break;
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleImageSelection(e.target.files[0]);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = '';
  };

  const handleImageSelection = (file: File) => {
    if (file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const isValidToSend = (message.trim() || selectedImage) && !disabled;

  return (
    <div className="flex flex-col px-4 py-3 gap-3">
      {/* Voice Status Indicator */}
      {isVoiceActive && (
        <div className="flex items-center gap-2 text-sm">
          {isProcessingImage ? (
            <>
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span className="text-orange-400">Voice session active • Voice recording paused while processing image</span>
            </>
          ) : (
            <>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400">Voice session active • Messages and images will also be sent to voice AI • Recording starts automatically after AI speaks</span>
            </>
          )}
        </div>
      )}

      {/* Image Preview */}
      {imagePreview && (
        <div className="flex items-center gap-3 bg-border rounded-lg p-3">
          <div className="relative">
            <img 
              src={imagePreview} 
              alt="Selected" 
              className="w-16 h-16 object-cover rounded-lg border border-gray-600"
            />
            <button
              type="button"
              onClick={clearImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
              disabled={disabled}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="flex-1">
            <p className="text-white text-sm font-medium">{selectedImage?.name}</p>
            <p className="text-text-secondary text-xs">
              {selectedImage && (selectedImage.size / 1024).toFixed(1)} KB
            </p>
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="flex items-end gap-3">
        <form onSubmit={handleSubmit} className="flex flex-col min-w-40 flex-1">
          <div className="flex w-full items-end rounded bg-border min-h-[48px]">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={selectedImage ? "Add a message (optional)" : 
                isProcessingImage ? "Processing image... Voice recording will resume after response" :
                isVoiceActive ? "Type your message or paste an image • Voice recording will start automatically after AI speaks" :
                "Type your message or paste an image"}
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border-none bg-border focus:border-none placeholder:text-text-secondary px-4 py-3 rounded-r-none border-r-0 pr-2 text-base font-normal leading-normal min-h-[48px] max-h-[120px]"
              disabled={disabled}
              rows={1}
            />
            <div className="flex border-none bg-border items-end justify-center pr-4 rounded-r border-l-0 !pr-2 pb-3">
              <div className="flex items-center gap-4 justify-end">
                <div className="flex items-center gap-1">
                  <label
                    data-tour="image-upload"
                    className={`flex items-center justify-center p-1.5 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-600 rounded transition-colors'}`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={disabled}
                    />
                    <Image className="w-5 h-5 text-text-secondary" />
                  </label>

                  {/* Voice Recording Button - Hide when processing image */}
                  {isVoiceActive && isUserRecording && !isProcessingImage && (
                    <button
                      type="button"
                      onClick={onStopRecording}
                      data-tour="stop-recording"
                      className="flex items-center justify-center w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full transition-colors animate-pulse"
                      title="Stop Recording"
                    >
                      <Stop className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="min-w-[40px] h-8 w-8 flex items-center justify-center rounded bg-primary text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                  disabled={!isValidToSend}
                  aria-label="Send"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M3.4 20.29a1 1 0 0 0 1.09.22l16-7a1 1 0 0 0 0-1.82l-16-7A1 1 0 0 0 3 5.18v4.55a1 1 0 0 0 .76.97l10.13 2.3-10.13 2.3A1 1 0 0 0 3 14.27v4.55a1 1 0 0 0 .4.82z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
